import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Wand2,
  Sparkles,
  Eraser,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Check,
  X,
  Loader2,
  ArrowLeftRight,
  Minus,
  Plus,
  Send,
  MessageSquare,
  RefreshCw,
  GripVertical
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Before/After Comparison Component - Horizontal slider reveal
const BeforeAfterComparison = ({ beforeImage, afterImage, position, onPositionChange }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Calculate position from mouse/touch event
  const calculatePosition = useCallback((clientX) => {
    if (!containerRef.current) return position;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    return percentage;
  }, [position]);

  // Handle mouse/touch events
  const handleStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    if (clientX !== undefined) {
      onPositionChange(calculatePosition(clientX));
    }
  };

  const handleMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    if (clientX !== undefined) {
      onPositionChange(calculatePosition(clientX));
    }
  }, [isDragging, calculatePosition, onPositionChange]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      document.body.style.userSelect = 'none';
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
      document.body.style.userSelect = '';
      document.body.style.overflow = '';
    };
  }, [isDragging, handleMove, handleEnd]);

  // Load image to get dimensions
  useEffect(() => {
    if (afterImage) {
      const img = new Image();
      img.onload = () => {
        const container = containerRef.current;
        if (container) {
          const maxWidth = container.parentElement?.clientWidth - 20 || 500;
          const maxHeight = 300;
          const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
          setImageSize({
            width: img.width * scale,
            height: img.height * scale
          });
        }
      };
      img.src = afterImage;
    }
  }, [afterImage]);

  return (
    <div 
      ref={containerRef}
      className="relative flex items-center justify-center select-none"
      style={{ minHeight: '300px', cursor: 'ew-resize' }}
      onMouseDown={handleStart}
      onTouchStart={handleStart}
    >
      {/* After image (full, on bottom) */}
      <div 
        className="relative"
        style={{ width: imageSize.width || 'auto', height: imageSize.height || 'auto' }}
      >
        <img 
          src={afterImage} 
          alt="After" 
          className="block"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          draggable={false}
        />
        
        {/* Before image (clipped from right) */}
        <div 
          className="absolute top-0 left-0 h-full overflow-hidden"
          style={{ width: `${position}%` }}
        >
          <img 
            src={beforeImage} 
            alt="Before" 
            style={{ 
              width: imageSize.width || 'auto', 
              height: '100%', 
              objectFit: 'contain',
              maxWidth: 'none'
            }}
            draggable={false}
          />
        </div>
        
        {/* Slider handle */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
          style={{ 
            left: `${position}%`, 
            transform: 'translateX(-50%)',
            pointerEvents: 'none'
          }}
        >
          {/* Handle button */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center border-2 border-gray-300"
          >
            <GripVertical className="w-5 h-5 text-gray-500" />
          </div>
        </div>
        
        {/* Labels */}
        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
          BEFORE
        </div>
        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
          AFTER
        </div>
      </div>
    </div>
  );
};

const AIStudioModal = ({ 
  isOpen, 
  onClose, 
  imageData, 
  onComplete,
  token 
}) => {
  // State management
  const [currentStep, setCurrentStep] = useState('initial'); // initial, processing, review
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState('');
  
  // AI options
  const [options, setOptions] = useState({
    background_replacement: true,
    surface_cleaning: true,
    debranding: true,
    upscale: true
  });
  
  // Custom AI Command state
  const [customCommand, setCustomCommand] = useState('');
  const [isProcessingCommand, setIsProcessingCommand] = useState(false);
  
  // Magic Eraser state
  const [eraserMode, setEraserMode] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [brushStrokes, setBrushStrokes] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Zoom/Pan state
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const lastPanPos = useRef({ x: 0, y: 0 });
  
  // Before/After slider
  const [comparePosition, setComparePosition] = useState(50);
  const [showComparison, setShowComparison] = useState(false);
  
  // Refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Initialize with image data
  useEffect(() => {
    if (isOpen && imageData) {
      setOriginalImage(imageData);
      setProcessedImage(null);
      setCurrentStep('initial');
      setProcessingProgress(0);
      setBrushStrokes([]);
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
      setCustomCommand('');
    }
  }, [isOpen, imageData]);

  // Run AI Pipeline
  const runAIPipeline = async () => {
    setCurrentStep('processing');
    setProcessingProgress(0);
    
    const steps = [
      { name: 'background_replacement', label: 'Removing Background...', weight: 25, endpoint: 'background-replacement' },
      { name: 'surface_cleaning', label: 'Making Product Brand New...', weight: 25, endpoint: 'surface-cleaning' },
      { name: 'debranding', label: 'Removing Text & Labels...', weight: 30 },
      { name: 'upscale', label: 'HD Enhancement...', weight: 20, endpoint: 'upscale' }
    ];
    
    try {
      let currentImage = originalImage;
      let progress = 0;
      
      for (const step of steps) {
        if (!options[step.name]) {
          progress += step.weight;
          setProcessingProgress(progress);
          continue;
        }
        
        setProcessingStep(step.label);
        
        // Special handling for de-branding
        if (step.name === 'debranding') {
          try {
            setProcessingStep('Detecting Text & Labels...');
            const detectResponse = await axios.post(
              `${API}/ai-studio/step/detect-text`,
              { image_data: currentImage },
              { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
            );
            
            if (detectResponse.data.success && detectResponse.data.detections?.length > 0) {
              setProcessingStep(`Removing ${detectResponse.data.count} elements...`);
              const debrandResponse = await axios.post(
                `${API}/ai-studio/step/debranding`,
                { 
                  image_data: currentImage,
                  detections: detectResponse.data.detections 
                },
                { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
              );
              
              if (debrandResponse.data.success) {
                currentImage = debrandResponse.data.image_data;
              }
            }
          } catch (debrandError) {
            console.warn('De-branding step failed, continuing...', debrandError.message);
            // Continue with current image if debranding fails
          }
          progress += step.weight;
          setProcessingProgress(progress);
          continue;
        }
        
        // Standard step processing with timeout
        try {
          const response = await axios.post(
            `${API}/ai-studio/step/${step.endpoint}`,
            { image_data: currentImage },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
          );
          
          if (response.data.success) {
            currentImage = response.data.image_data;
          } else {
            console.warn(`Step ${step.name} failed:`, response.data.error);
          }
        } catch (stepError) {
          console.warn(`Step ${step.name} error, continuing with current image:`, stepError.message);
          // Continue with current image if step fails
        }
        
        progress += step.weight;
        setProcessingProgress(progress);
      }
      
      setProcessedImage(currentImage);
      setCurrentStep('review');
      toast.success('AI processing complete!');
      
    } catch (error) {
      console.error('AI Pipeline error:', error);
      toast.error('AI processing failed: ' + (error.response?.data?.detail || error.message));
      setCurrentStep('initial');
    }
  };

  // Process custom AI command
  const processCustomCommand = async () => {
    if (!customCommand.trim()) {
      toast.error('Please enter a command');
      return;
    }
    
    setIsProcessingCommand(true);
    
    try {
      // Send custom command to backend
      const response = await axios.post(
        `${API}/ai-studio/custom-command`,
        { 
          image_data: processedImage,
          command: customCommand 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setProcessedImage(response.data.image_data);
        setCustomCommand('');
        toast.success('Command applied!');
      } else {
        throw new Error(response.data.error || 'Command failed');
      }
    } catch (error) {
      console.error('Custom command error:', error);
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessingCommand(false);
    }
  };

  // Skip AI and go straight to original
  const skipAI = () => {
    setProcessedImage(originalImage);
    setCurrentStep('review');
  };

  // Magic Eraser functions
  const startDrawing = (e) => {
    if (!eraserMode) return;
    setIsDrawing(true);
    const pos = getCanvasPosition(e);
    setBrushStrokes(prev => [...prev, { points: [pos], size: brushSize }]);
  };

  const draw = (e) => {
    if (!isDrawing || !eraserMode) return;
    const pos = getCanvasPosition(e);
    setBrushStrokes(prev => {
      const newStrokes = [...prev];
      const lastStroke = newStrokes[newStrokes.length - 1];
      if (lastStroke) {
        lastStroke.points.push(pos);
      }
      return newStrokes;
    });
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const getCanvasPosition = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
    
    return {
      x: (clientX - rect.left - panOffset.x) / zoom,
      y: (clientY - rect.top - panOffset.y) / zoom
    };
  };

  // Apply Magic Eraser
  const applyMagicEraser = async () => {
    if (brushStrokes.length === 0) {
      toast.info('No areas marked for erasing');
      return;
    }
    
    setProcessingStep('Applying Magic Eraser...');
    setCurrentStep('processing');
    
    try {
      const maskCanvas = document.createElement('canvas');
      const img = new Image();
      await new Promise((resolve) => {
        img.onload = resolve;
        img.src = processedImage;
      });
      
      maskCanvas.width = img.width;
      maskCanvas.height = img.height;
      const maskCtx = maskCanvas.getContext('2d');
      
      maskCtx.fillStyle = 'black';
      maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
      
      maskCtx.strokeStyle = 'white';
      maskCtx.lineCap = 'round';
      maskCtx.lineJoin = 'round';
      
      const displayCanvas = canvasRef.current;
      const scaleX = img.width / (displayCanvas?.width || img.width);
      const scaleY = img.height / (displayCanvas?.height || img.height);
      
      for (const stroke of brushStrokes) {
        maskCtx.lineWidth = stroke.size * Math.max(scaleX, scaleY);
        maskCtx.beginPath();
        
        for (let i = 0; i < stroke.points.length; i++) {
          const point = stroke.points[i];
          const scaledX = point.x * scaleX;
          const scaledY = point.y * scaleY;
          
          if (i === 0) {
            maskCtx.moveTo(scaledX, scaledY);
          } else {
            maskCtx.lineTo(scaledX, scaledY);
          }
        }
        maskCtx.stroke();
      }
      
      const maskData = maskCanvas.toDataURL('image/png');
      
      const response = await axios.post(
        `${API}/ai-studio/magic-eraser`,
        { 
          image_data: processedImage,
          mask_data: maskData 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setProcessedImage(response.data.image_data);
        setBrushStrokes([]);
        toast.success('Magic Eraser applied!');
      } else {
        throw new Error(response.data.error || 'Eraser failed');
      }
      
    } catch (error) {
      console.error('Magic Eraser error:', error);
      toast.error('Magic Eraser failed: ' + error.message);
    } finally {
      setCurrentStep('review');
    }
  };

  const clearStrokes = () => {
    setBrushStrokes([]);
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(z => Math.min(z * 1.5, 5));
  const handleZoomOut = () => setZoom(z => Math.max(z / 1.5, 0.5));
  const handleResetView = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Pan controls
  const handlePanStart = (e) => {
    if (eraserMode) return;
    setIsPanning(true);
    lastPanPos.current = {
      x: e.clientX || e.touches?.[0]?.clientX || 0,
      y: e.clientY || e.touches?.[0]?.clientY || 0
    };
  };

  const handlePanMove = (e) => {
    if (!isPanning || eraserMode) return;
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
    
    setPanOffset(prev => ({
      x: prev.x + (currentX - lastPanPos.current.x),
      y: prev.y + (currentY - lastPanPos.current.y)
    }));
    
    lastPanPos.current = { x: currentX, y: currentY };
  };

  const handlePanEnd = () => {
    setIsPanning(false);
  };

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !processedImage) return;
    
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const container = containerRef.current;
      if (container) {
        const maxWidth = container.clientWidth - 40;
        const maxHeight = 350;
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
        
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      if (brushStrokes.length > 0) {
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        for (const stroke of brushStrokes) {
          ctx.lineWidth = stroke.size;
          ctx.beginPath();
          
          for (let i = 0; i < stroke.points.length; i++) {
            if (i === 0) {
              ctx.moveTo(stroke.points[i].x, stroke.points[i].y);
            } else {
              ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
            }
          }
          ctx.stroke();
        }
      }
    };
    
    img.src = processedImage;
  }, [processedImage, brushStrokes]);

  useEffect(() => {
    if (currentStep === 'review') {
      drawCanvas();
    }
  }, [currentStep, processedImage, brushStrokes, drawCanvas]);

  // Confirm and proceed
  const handleConfirm = () => {
    onComplete(processedImage);
    onClose();
  };

  // Render content based on step
  const renderContent = () => {
    switch (currentStep) {
      case 'initial':
        return (
          <div className="space-y-6">
            {/* Preview Image */}
            <div className="relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '200px' }}>
              {originalImage && (
                <img 
                  src={originalImage} 
                  alt="Original" 
                  className="max-h-56 object-contain"
                />
              )}
            </div>
            
            {/* AI Options */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 space-y-4 border border-blue-200">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-500" />
                Gemini Nano Banana - Pro Studio
              </h4>
              <p className="text-xs text-gray-500">AI-powered professional e-commerce photography</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm">
                  <div>
                    <Label htmlFor="bg-replacement" className="text-sm font-medium">Remove Background</Label>
                    <p className="text-xs text-gray-400">Pure white #FFFFFF</p>
                  </div>
                  <Switch 
                    id="bg-replacement"
                    checked={options.background_replacement}
                    onCheckedChange={(v) => setOptions(o => ({...o, background_replacement: v}))}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm">
                  <div>
                    <Label htmlFor="surface-clean" className="text-sm font-medium">Make Brand New</Label>
                    <p className="text-xs text-gray-400">Clean, polish, fix</p>
                  </div>
                  <Switch 
                    id="surface-clean"
                    checked={options.surface_cleaning}
                    onCheckedChange={(v) => setOptions(o => ({...o, surface_cleaning: v}))}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm">
                  <div>
                    <Label htmlFor="debranding" className="text-sm font-medium">Remove Text/Labels</Label>
                    <p className="text-xs text-gray-400">OCR + seamless fill</p>
                  </div>
                  <Switch 
                    id="debranding"
                    checked={options.debranding}
                    onCheckedChange={(v) => setOptions(o => ({...o, debranding: v}))}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm">
                  <div>
                    <Label htmlFor="upscale" className="text-sm font-medium">HD Enhancement</Label>
                    <p className="text-xs text-gray-400">Sharp & crisp output</p>
                  </div>
                  <Switch 
                    id="upscale"
                    checked={options.upscale}
                    onCheckedChange={(v) => setOptions(o => ({...o, upscale: v}))}
                  />
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={skipAI}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Skip AI Polish
              </Button>
              <Button 
                onClick={runAIPipeline}
                className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Apply Nano Banana Polish
              </Button>
            </div>
          </div>
        );
        
      case 'processing':
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-6">
            <div className="relative">
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-lg text-gray-900">{processingStep || 'Processing...'}</h3>
              <p className="text-sm text-gray-500">Please wait while AI polishes your image</p>
            </div>
            <div className="w-full max-w-xs">
              <Progress value={processingProgress} className="h-2" />
              <p className="text-xs text-center text-gray-400 mt-1">{Math.round(processingProgress)}%</p>
            </div>
          </div>
        );
        
      case 'review':
        return (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between bg-gray-100 rounded-lg p-2 gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant={eraserMode ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setEraserMode(!eraserMode)}
                  className={eraserMode ? "bg-red-500 hover:bg-red-600" : ""}
                >
                  <Eraser className="w-4 h-4 mr-1" />
                  Eraser
                </Button>
                
                {eraserMode && (
                  <>
                    <div className="flex items-center gap-1 border-l pl-2">
                      <Button variant="ghost" size="sm" onClick={() => setBrushSize(s => Math.max(5, s - 5))}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="text-xs w-6 text-center">{brushSize}</span>
                      <Button variant="ghost" size="sm" onClick={() => setBrushSize(s => Math.min(50, s + 5))}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    {brushStrokes.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearStrokes}>
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant={showComparison ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setShowComparison(!showComparison)}
                >
                  <ArrowLeftRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleZoomOut}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-xs w-10 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="ghost" size="sm" onClick={handleZoomIn}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleResetView}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Canvas Container */}
            <div 
              ref={containerRef}
              className="relative bg-gray-100 rounded-lg overflow-hidden select-none"
              style={{ 
                minHeight: '300px',
                cursor: eraserMode ? `crosshair` : (showComparison ? 'ew-resize' : (isPanning ? 'grabbing' : 'grab'))
              }}
            >
              {showComparison ? (
                <BeforeAfterComparison
                  beforeImage={originalImage}
                  afterImage={processedImage}
                  position={comparePosition}
                  onPositionChange={setComparePosition}
                />
              ) : (
                <div 
                  className="flex items-center justify-center p-2"
                  style={{ 
                    transform: `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
                    transformOrigin: 'center center'
                  }}
                  onMouseDown={eraserMode ? startDrawing : handlePanStart}
                  onMouseMove={eraserMode ? draw : handlePanMove}
                  onMouseUp={eraserMode ? stopDrawing : handlePanEnd}
                  onMouseLeave={eraserMode ? stopDrawing : handlePanEnd}
                  onTouchStart={eraserMode ? startDrawing : handlePanStart}
                  onTouchMove={eraserMode ? draw : handlePanMove}
                  onTouchEnd={eraserMode ? stopDrawing : handlePanEnd}
                >
                  <canvas 
                    ref={canvasRef}
                    className="max-w-full"
                  />
                </div>
              )}
            </div>
            
            {/* Custom AI Command Input */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-gray-700">Give AI another command</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={customCommand}
                  onChange={(e) => setCustomCommand(e.target.value)}
                  placeholder="e.g., Remove the top product, Improve lighting, Make colors brighter..."
                  className="flex-1 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && processCustomCommand()}
                  disabled={isProcessingCommand}
                />
                <Button 
                  size="sm"
                  onClick={processCustomCommand}
                  disabled={isProcessingCommand || !customCommand.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isProcessingCommand ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Examples: "Remove scratches", "Make background brighter", "Regenerate with better quality"
              </p>
            </div>
            
            {/* Eraser instructions */}
            {eraserMode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-700">
                <strong>Eraser Mode:</strong> Paint over areas to remove. Click "Apply" when done.
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              {eraserMode && brushStrokes.length > 0 ? (
                <>
                  <Button variant="outline" onClick={() => setEraserMode(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={applyMagicEraser} className="flex-1 bg-red-500 hover:bg-red-600">
                    <Eraser className="w-4 h-4 mr-2" />
                    Apply Eraser
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setCurrentStep('initial')} className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Re-process
                  </Button>
                  <Button onClick={handleConfirm} className="flex-1 bg-green-600 hover:bg-green-700">
                    <Check className="w-4 h-4 mr-2" />
                    Confirm & Use Image
                  </Button>
                </>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-blue-500" />
            AI Studio Polish
          </DialogTitle>
          <DialogDescription>
            {currentStep === 'initial' && 'Choose options to professionally enhance your product image'}
            {currentStep === 'processing' && 'AI is processing your image...'}
            {currentStep === 'review' && 'Review and refine. Use commands or eraser for adjustments.'}
          </DialogDescription>
        </DialogHeader>
        
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default AIStudioModal;
